import { useState } from 'react';
import { format } from 'date-fns';
import { UserRoles } from '@/enums/enums';
import { useTenants } from '@/features/tenant/tenant.queries';
import { getUserOptions } from '@/features/user/user.queries';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Plus } from 'lucide-react';
import { z } from 'zod';

import { TenantAttributes } from '@/types/tenant.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RoomDateColumn } from '@/components/datatable/columns/rooms/rooms-columns';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import { EditTenantAttributesModal } from '@/components/modals/edit-tenant-attributes-modal';
import TenantCreateUpdateModal from '@/components/modals/tenant-create-update-modal';

const tenantRouteSchema = z.object({
  mode: z.enum(['create', 'update']).optional(),
  tenant: z.number().optional(),
});

export const Route = createFileRoute('/_app/(tenants)/tenants/')({
  validateSearch: zodSearchValidator(tenantRouteSchema),
  component: TenantsPage,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (![UserRoles.Root].includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function TenantsPage() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <TenantsTabHeader />
      <TenantsTable />
    </div>
  );
}

function TenantsTabHeader() {
  const navigate = useNavigate();

  const createRoom = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>Tenants</span>
        <span className='text-base font-light text-neutral-500'>Tenants</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={createRoom} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          Tenant
        </Button>
      </div>
      <TenantCreateUpdateModal />
    </div>
  );
}

function TenantsTable() {
  const navigate = useNavigate();
  const { data = [], isLoading } = useTenants();
  const [attributesTarget, setAttributesTarget] = useState<{
    id: number;
    domainName: string;
  } | null>(null);

  const updateRoom = (id: number) => {
    navigate({ to: '.', search: { mode: 'update', room: id } });
  };

  return (
    <>
      <DataTableSimple
        data={data}
        columns={['id', 'domainName', 'attributes', 'activeUntilDate', 'createdAt', 'updatedAt']}
        isLoading={isLoading}
        columnsLabels={{
          id: 'Serial No',
          domainName: 'Domain Name',
          attributes: 'Modules',
          activeUntilDate: 'Active Until Date',
          createdAt: 'Created At',
          updatedAt: 'Updated At',
        }}
        customRender={{
          attributes: (row) => {
            const attrs = row.getValue('attributes') as TenantAttributes | null;
            const modules = attrs?.modules ?? [];
            if (modules.length === 0) {
              return <span className='text-sm text-muted-foreground'>—</span>;
            }
            return (
              <div className='flex flex-wrap gap-1'>
                {modules.map((mod) => (
                  <Badge key={mod} variant='secondary' className='text-xs'>
                    {mod}
                  </Badge>
                ))}
              </div>
            );
          },
          activeUntilDate: (row) => {
            const val = row.getValue('activeUntilDate') as string | null;
            if (!val) return <span className='text-sm text-muted-foreground'>—</span>;
            return <span>{format(new Date(val), 'dd/MM/yyyy')}</span>;
          },
          createdAt: (row) => <RoomDateColumn date={row.getValue('createdAt')} />,
          updatedAt: (row) => <RoomDateColumn date={row.getValue('updatedAt')} />,
        }}
        extraColumns={[
          {
            id: 'actions',
            size: 50,
            cell: ({ row }) => (
              <DataTableRowActions
                row={row}
                menuItems={[
                  {
                    label: 'Edit',
                    onClick: () => updateRoom(row.getValue('id')),
                    isLoading: false,
                  },
                  {
                    label: 'Edit Attributes',
                    onClick: () =>
                      setAttributesTarget({
                        id: row.getValue('id'),
                        domainName: row.getValue('domainName'),
                      }),
                    isLoading: false,
                  },
                ]}
              />
            ),
          },
        ]}
        className='flex h-full flex-col overflow-y-auto'
      />

      {attributesTarget && (
        <EditTenantAttributesModal
          open={!!attributesTarget}
          onOpenChange={(open) => !open && setAttributesTarget(null)}
          tenantId={attributesTarget.id}
          tenantName={attributesTarget.domainName}
        />
      )}
    </>
  );
}
